"use client"

import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { Loader } from "@/app/components/loader"

export interface TosBlockingModalProps {
  shouldShowModal: boolean
  submitting: boolean
  error: string | null
  onAccept: () => Promise<void>
}

/**
 * Full-screen, non-dismissible modal for TOS acceptance by existing authenticated users.
 *
 * Shows when the backend indicates the user has not accepted the current TOS version.
 * Cannot be dismissed until acceptance is recorded successfully.
 *
 * This is a pure UI component - all logic is handled by useTosAcceptanceFlow hook.
 *
 * Related to: TASK-1012
 */
export function TosBlockingModal(props: TosBlockingModalProps) {
  return <TosBlockingModalContent {...props} />
}

/**
 * Internal component containing the modal UI.
 * Extracted to ensure hooks are called unconditionally.
 */
function TosBlockingModalContent({
  shouldShowModal,
  submitting,
  error,
  onAccept,
}: TosBlockingModalProps) {
  const [checked, setChecked] = useState(false)

  if (!shouldShowModal) {
    return null
  }

  return (
    <Dialog open={shouldShowModal} onOpenChange={() => {}}>
      <DialogContent
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="sm:max-w-md [&>button]:hidden"
      >
        <DialogHeader>
          <DialogTitle>Terms of Use and Privacy Policy</DialogTitle>
          <DialogDescription>
            We&apos;ve updated our Terms of Use and Privacy Policy. Please review and accept them to
            continue using Devplan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            You must accept to continue using the app.
          </p>

          <div className="flex items-start gap-2 text-sm">
            <Checkbox
              id="tos-blocking-checkbox"
              checked={checked}
              onCheckedChange={(v) => setChecked(v === true)}
              className="mt-0.5"
            />
            <label htmlFor="tos-blocking-checkbox" className="cursor-pointer leading-tight">
              I have read and agree to the{" "}
              <a
                href="https://www.devplan.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Use
              </a>{" "}
              and{" "}
              <a
                href="https://www.devplan.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </a>
              .
            </label>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            onClick={onAccept}
            disabled={!checked || submitting}
            className="w-full sm:w-auto"
          >
            {submitting && <Loader />}
            Accept and Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
